import type { ComponentPropsWithoutRef } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Archive,
  BadgeCheck,
  Bell,
  Ban,
  BarChart3,
  Bug,
  Building2,
  CalendarCheck,
  CalendarX,
  CalendarRange,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleCheck,
  CirclePlus,
  ClipboardCheck,
  Clock,
  CloudCheck,
  CloudOff,
  CloudSync,
  Columns,
  Database,
  Download,
  Droplets,
  EllipsisVertical,
  Factory,
  FilePenLine,
  FilePlus,
  FileText,
  Filter,
  Flame,
  GripVertical,
  Headset,
  Home,
  Hourglass,
  ImagePlus,
  Inbox,
  Info,
  Image,
  Kanban,
  LayoutGrid,
  Link,
  List,
  ListPlus,
  MoreVertical,
  PanelsTopLeft,
  Play,
  Loader2,
  Mail,
  MessageSquare,
  Menu,
  Minus,
  MoreHorizontal,
  Pencil,
  Phone,
  PlayCircle,
  Plus,
  Radar,
  RefreshCw,
  RotateCcw,
  Search,
  Settings,
  Shield,
  Siren,
  SlidersHorizontal,
  Snowflake,
  SquareCheckBig,
  StickyNote,
  Thermometer,
  Trash2,
  Upload,
  User,
  UserX,
  Users,
  Wind,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Icon name to Lucide component mapping.
 *
 * IMPORTANT: Use `satisfies` to preserve literal types for IconName.
 * This ensures TypeScript catches invalid icon names at compile time.
 *
 * To add a new icon:
 * 1. Import the Lucide icon at the top of this file
 * 2. Add a mapping entry below (e.g., my_icon: MyIcon)
 */
const ICON_MAP = {
  ac_unit: Snowflake,
  add: Plus,
  add_business: Building2,
  add_circle: CirclePlus,
  add_photo_alternate: ImagePlus,
  air: Wind,
  account_tree: LayoutGrid,
  assignment_turned_in: ClipboardCheck,
  arrow_forward: ArrowRight,
  block: Ban,
  build: Wrench,
  check: Check,
  check_circle: CheckCircle2,
  chevron_left: ChevronLeft,
  chevron_right: ChevronRight,
  close: X,
  cloud_done: CloudCheck,
  cloud_off: CloudOff,
  cloud_sync: CloudSync,
  corporate_fare: Building2,
  database: Database,
  date_range: CalendarRange,
  delete: Trash2,
  description: FileText,
  device_thermostat: Thermometer,
  download_for_offline: Download,
  drag_indicator: GripVertical,
  edit: Pencil,
  edit_document: FilePenLine,
  edit_note: FilePenLine,
  ellipsis_vertical: EllipsisVertical,
  emergency: Siren,
  error: AlertCircle,
  error_outline: CircleAlert,
  expand_more: ChevronDown,
  expand_less: ChevronUp,
  factory: Factory,
  filter_list: Filter,
  group: Users,
  group_off: UserX,
  hourglass_empty: Hourglass,
  history: Clock,
  home: Home,
  inbox: Inbox,
  info: Info,
  insights: BarChart3,
  library_books: FileText,
  link: Link,
  local_fire_department: Flame,
  panels_top_left: PanelsTopLeft,
  menu: Menu,
  mail: Mail,
  message: MessageSquare,
  more_horiz: MoreHorizontal,
  more_vert: MoreVertical,
  notifications: Bell,
  notification_important: Bell,
  note_add: FilePlus,
  archive: Archive,
  event: CalendarRange,
  event_available: CalendarCheck,
  event_busy: CalendarX,
  pending: Clock,
  person: User,
  phone_enabled: Phone,
  photo: Image,
  play_arrow: Play,
  play_circle: PlayCircle,
  playlist_add: ListPlus,
  priority_high: AlertTriangle,
  progress_activity: Loader2,
  radio_button_checked: Circle,
  refresh: RefreshCw,
  remove: Minus,
  replay: RotateCcw,
  report: Bug,
  safety_system: Shield,
  schedule: Clock,
  search: Search,
  sensors: Radar,
  settings: Settings,
  settings_backup_restore: RefreshCw,
  support: Headset,
  sync: CloudSync,
  offline_pin: CloudOff,
  science: Info,
  domain: Building2,
  sticky_note_2: StickyNote,
  badge: BadgeCheck,
  square_check_big: SquareCheckBig,
  task_alt: BadgeCheck,
  tune: SlidersHorizontal,
  upload_file: Upload,
  verified: CircleCheck,
  view_column: Columns,
  view_kanban: Kanban,
  view_list: List,
  view_module: LayoutGrid,
  warning: AlertTriangle,
  water: Droplets,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICON_MAP;

interface IconProps extends ComponentPropsWithoutRef<'svg'> {
  name: IconName;
}

const ICON_FALLBACK: IconName = 'info';
const missingIconNames = new Set<string>();

export function isIconName(value: string): value is IconName {
  return value in ICON_MAP;
}

export function toIconName(
  value: string | undefined,
  fallback: IconName = ICON_FALLBACK
): IconName {
  if (value && value in ICON_MAP) return value as IconName;
  if (import.meta.env.DEV && value && !missingIconNames.has(value)) {
    missingIconNames.add(value);
    console.warn(`[Icon] Unknown icon name "${value}". Falling back to "${fallback}".`);
  }
  return fallback;
}

export function Icon({ name, className, ...props }: IconProps) {
  const IconComponent = ICON_MAP[name] ?? ICON_MAP[ICON_FALLBACK];
  const ariaHidden = props['aria-label'] || props['aria-labelledby'] ? undefined : true;

  return (
    <IconComponent
      className={cn('inline-block', className)}
      aria-hidden={ariaHidden}
      size="1em"
      {...props}
    />
  );
}
